 import { useState, useEffect, useMemo, useCallback } from "react";
 import { supabase } from "@/integrations/supabase/client";
 import { useRestaurantContext } from "@/contexts/RestaurantContext";
 import {
   calculateSuggestedPrice,
   calculateCMVPercentage,
   calculateGrossMargin,
   calculateRealMarginPercent,
   calculateContributionMargin,
   calculateEstimatedProfit,
   getMarginStatus,
   type MarginStatus,
 } from "@/lib/pricing-calculations";
 
 export interface PricingData {
   // Custo do Prato
   ingredientCost: number;
   cmvValue: number;
   cmvPercentage: number;
   
   // Precificação
   currentPrice: number;
   suggestedPrice: number;
   realMarginPercent: number;
   grossMargin: number;
   contributionMargin: number;
   
   // Custos Operacionais (médias históricas)
   avgCardFeePercent: number;
   avgFixedExpensePercent: number;
   estimatedOperationalCost: number;
   estimatedProfit: number;
   estimatedProfitPercent: number;
   
   // Status
   marginStatus: MarginStatus;
 }
 
 interface OperationalAverages {
   avgCardFeePercent: number;
   avgFixedExpensePercent: number;
   fixedExpensePerUnit: number;
   isLoading: boolean;
 }
 
 export function usePricingCalculations(
   ingredientCost: number,
   currentPrice: number,
   targetMarginPercent: number
 ) {
   const { activeRestaurant } = useRestaurantContext();
   const [operationalAverages, setOperationalAverages] = useState<OperationalAverages>({
     avgCardFeePercent: 0,
     avgFixedExpensePercent: 0,
     fixedExpensePerUnit: 0,
     isLoading: true,
   });
 
   // Busca médias históricas do último mês
   const fetchOperationalAverages = useCallback(async () => {
     if (!activeRestaurant) {
       setOperationalAverages({
         avgCardFeePercent: 0,
         avgFixedExpensePercent: 0,
         fixedExpensePerUnit: 0,
         isLoading: false,
       });
       return;
     }
 
     try {
       // Data de 30 dias atrás
       const thirtyDaysAgo = new Date();
       thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
       const startDate = thirtyDaysAgo.toISOString();
 
       // Buscar todas as informações em paralelo
       const [paymentMethodsResult, ordersResult, expensesResult] = await Promise.all([
         // Formas de pagamento para média de taxa
         supabase
           .from("payment_methods")
           .select("fee_percentage, type")
           .eq("restaurant_id", activeRestaurant.id)
           .eq("is_active", true),
         
         // Pedidos do período para calcular receita e quantidade
         supabase
           .from("orders")
           .select("total, status")
           .eq("restaurant_id", activeRestaurant.id)
           .gte("created_at", startDate)
          .in("status", ["delivered"]),
         
         // Despesas fixas do período (contas classificadas como despesa fixa)
         supabase
           .from("expenses")
           .select("amount")
           .eq("restaurant_id", activeRestaurant.id)
           .gte("expense_date", startDate.split("T")[0]),
       ]);
 
       // Calcular média de taxa de cartão
       const cardMethods = paymentMethodsResult.data?.filter(m => m.type === "card") || [];
       const avgCardFee = cardMethods.length > 0
         ? cardMethods.reduce((sum, m) => sum + Number(m.fee_percentage || 0), 0) / cardMethods.length
         : 2.5; // Fallback padrão
 
       // Calcular receita total e quantidade de pedidos
       const orders = ordersResult.data || [];
       const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
       const totalOrders = orders.length;
 
       // Calcular total de despesas fixas
       const expenses = expensesResult.data || [];
       const totalFixedExpenses = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
 
       // Percentual de despesas fixas sobre receita
       const fixedExpensePercent = totalRevenue > 0
         ? (totalFixedExpenses / totalRevenue) * 100
         : 0;
 
       // Rateio de fixas por unidade vendida
       const fixedExpensePerUnit = totalOrders > 0
         ? totalFixedExpenses / totalOrders
         : 0;
 
       setOperationalAverages({
         avgCardFeePercent: avgCardFee,
         avgFixedExpensePercent: fixedExpensePercent,
         fixedExpensePerUnit,
         isLoading: false,
       });
     } catch (error) {
       console.error("Error fetching operational averages:", error);
       setOperationalAverages({
         avgCardFeePercent: 2.5,
         avgFixedExpensePercent: 0,
         fixedExpensePerUnit: 0,
         isLoading: false,
       });
     }
   }, [activeRestaurant]);
 
   useEffect(() => {
     fetchOperationalAverages();
   }, [fetchOperationalAverages]);
 
   // Cálculos derivados (recalculam quando inputs mudam)
   const pricingData: PricingData = useMemo(() => {
     const { avgCardFeePercent, avgFixedExpensePercent, fixedExpensePerUnit } = operationalAverages;
     
     const cmvValue = ingredientCost;
     const cmvPercentage = calculateCMVPercentage(ingredientCost, currentPrice);
     const suggestedPrice = calculateSuggestedPrice(ingredientCost, targetMarginPercent);
     const realMarginPercent = calculateRealMarginPercent(currentPrice, ingredientCost);
     const grossMargin = calculateGrossMargin(currentPrice, ingredientCost);
     const contributionMargin = calculateContributionMargin(currentPrice, ingredientCost, avgCardFeePercent);
     
     const variableExpense = currentPrice * (avgCardFeePercent / 100);
     const estimatedOperationalCost = variableExpense + fixedExpensePerUnit;
     const estimatedProfit = calculateEstimatedProfit(
       currentPrice,
       ingredientCost,
       avgCardFeePercent,
       fixedExpensePerUnit
     );
     const estimatedProfitPercent = currentPrice > 0 ? (estimatedProfit / currentPrice) * 100 : 0;
     
     const marginStatus = getMarginStatus(realMarginPercent, targetMarginPercent);
 
     return {
       ingredientCost,
       cmvValue,
       cmvPercentage,
       currentPrice,
       suggestedPrice,
       realMarginPercent,
       grossMargin,
       contributionMargin,
       avgCardFeePercent,
       avgFixedExpensePercent,
       estimatedOperationalCost,
       estimatedProfit,
       estimatedProfitPercent,
       marginStatus,
     };
   }, [ingredientCost, currentPrice, targetMarginPercent, operationalAverages]);
 
   return {
     ...pricingData,
     isLoadingAverages: operationalAverages.isLoading,
     fixedExpensePerUnit: operationalAverages.fixedExpensePerUnit,
     refetchAverages: fetchOperationalAverages,
   };
 }